import { postFormData, postJsonWithOptions } from "../api/api";
import { getSession } from "../session/session";
import type { DayAvailability } from "../types/provider-registration";
import {
	clearPendingProviderCategories,
	savePendingProviderCategories,
} from "./provider-categories-pending";
import { savePendingProviderSchedule } from "./provider-schedule-pending";

type CreateUserResponse = { user: { id: number } };

type CreateClientResponse = { client: { id: number } };

type CreateProviderResponse = { provider: { id: number } };

type CreateAddressPayload = {
	user_id: number;
	name_country: string;
	name_state: string;
	name_city: string;
	name_address: string;
	street_address: string;
	reference_address: string;
};

function extractEntityId(
	response: unknown,
	entityKey: "user" | "client" | "provider",
): number {
	if (!response || typeof response !== "object") {
		throw new Error(`Respuesta inválida al crear ${entityKey}`);
	}

	const record = response as Record<string, unknown>;
	const nested = record[entityKey];

	if (nested && typeof nested === "object") {
		const nestedId = (nested as { id?: unknown }).id;
		if (typeof nestedId === "number" && Number.isFinite(nestedId)) return nestedId;
	}

	const flatId = record.id;
	if (typeof flatId === "number" && Number.isFinite(flatId)) return flatId;

	throw new Error(
		`No se pudo obtener el id de ${entityKey} en la respuesta del servidor`,
	);
}

function normalizeAddressText(rawAddress: string): string {
	return rawAddress
		.replace(/\s+/g, " ")
		.replace(/\s*,\s*/g, ", ")
		.replace(/,+/g, ",")
		.replace(/^,\s*|\s*,$/g, "")
		.trim();
}

function parseAddressParts(displayName: string) {
	const segments = displayName
		.split(",")
		.map((segment) => segment.trim())
		.filter(Boolean)
		.filter((segment) => !/^\d{3,}$/.test(segment));

	const country = segments.length > 0 ? segments[segments.length - 1] : "";
	const state = segments.length > 1 ? segments[segments.length - 2] : "";
	const city = segments.length > 2 ? segments[segments.length - 3] : "";
	const street = segments.length > 3 ? segments[0] : "";

	return { country, state, city, street };
}

function buildRegistrationAddressPayload(
	userId: number,
	rawAddress: string,
): CreateAddressPayload {
	const normalizedAddress = normalizeAddressText(rawAddress);
	const parts = parseAddressParts(normalizedAddress);
	const resolvedCountry = parts.country || "Venezuela";
	const resolvedState = parts.state || "No especificado";
	const resolvedCity = parts.city || "No especificado";
	const resolvedStreet = parts.street || normalizedAddress || "No especificado";
	const resolvedNameAddress =
		[resolvedCity, resolvedState]
			.filter((part) => part !== "No especificado")
			.join(", ") || "Dirección principal";
	const resolvedReference =
		[resolvedCity, resolvedState, resolvedCountry].filter(Boolean).join(", ") ||
		"N/A";

	return {
		user_id: userId,
		name_country: resolvedCountry,
		name_state: resolvedState,
		name_city: resolvedCity,
		name_address: resolvedNameAddress,
		street_address: resolvedStreet,
		reference_address: resolvedReference,
	};
}

async function persistStructuredAddress(userId: number, rawAddress: string) {
	const normalizedAddress = normalizeAddressText(rawAddress);
	if (!normalizedAddress) return;

	const payload = buildRegistrationAddressPayload(userId, normalizedAddress);
	await postJsonWithOptions("/addresses", payload, { auth: false });
}

export type RegisterClientInput = {
	names: string;
	lastNames: string;
	password: string;
	email: string;
	phone: string;
	age?: number;
	address: string;
	dateOfBirth?: string;
	gender?: "Masculino" | "Femenino" | "Otro";
	photoFile?: File;
};

export async function registerClient(
	input: RegisterClientInput,
): Promise<{ userId: number; clientId: number }> {
	const normalizedAddress = normalizeAddressText(input.address);
	const userForm = new FormData();
	userForm.append("names", input.names);
	userForm.append("last_names", input.lastNames);
	userForm.append("password", input.password);
	userForm.append("email", input.email);
	// type_user viene del query param ?type=Cliente en /users/register
	userForm.append("phone", input.phone);
	if (input.age !== undefined) {
		userForm.append("age", String(input.age));
	}
	userForm.append("addresses_user", normalizedAddress || input.address);
	if (input.photoFile) {
		userForm.append("image_profile", input.photoFile);
	}

	const userRes = await postFormData<CreateUserResponse>(
		"/users/register?type=Cliente",
		userForm,
	);
	const createdUserId = extractEntityId(userRes, "user");

	const clientRes = await postJsonWithOptions<CreateClientResponse>(
		"/clients",
		{ user_id: createdUserId },
		{ auth: false },
	);
	const createdClientId = extractEntityId(clientRes, "client");

	await persistStructuredAddress(createdUserId, normalizedAddress);

	return { userId: createdUserId, clientId: createdClientId };
}

export type RegisterProviderInput = {
	names: string;
	lastNames: string;
	password: string;
	email: string;
	phone: string;
	age?: number;
	address: string;
	dateOfBirth?: string;
	gender?: "Masculino" | "Femenino" | "Otro";
	companyName: string;
	description?: string;
	experienceYears?: number;
	categoryIds?: number[];
	availability?: DayAvailability[];
	photoFile?: File;
};

const PROVIDER_DAY_MAP: Record<
	DayAvailability["day"],
	"Lunes" | "Martes" | "Miercoles" | "Jueves" | "Viernes" | "Sabado" | "Domingo"
> = {
	Lunes: "Lunes",
	Martes: "Martes",
	Miércoles: "Miercoles",
	Jueves: "Jueves",
	Viernes: "Viernes",
	Sábado: "Sabado",
	Domingo: "Domingo",
};

function toTime24(value: string, period: "AM" | "PM"): string | null {
	const match = /^(0?[1-9]|1[0-2]):([0-5]\d)$/.exec(value.trim());
	if (!match) return null;
	const baseHour = Number(match[1]);
	const minute = Number(match[2]);
	if (!Number.isFinite(baseHour) || !Number.isFinite(minute)) return null;
	const hour = period === "PM" ? (baseHour % 12) + 12 : baseHour % 12;
	return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function isUnauthorizedError(err: unknown): boolean {
	if (!(err instanceof Error)) return false;
	const normalized = err.message.toLowerCase();
	return (
		normalized.includes("unauthorized") ||
		normalized.includes("no autorizado") ||
		normalized.includes("usuario no autenticado") ||
		normalized.includes("http 401")
	);
}

function normalizeCategoryIds(categoryIds?: Array<number | string>): number[] {
	if (!Array.isArray(categoryIds)) return [];
	return Array.from(
		new Set(
			categoryIds
				.map((categoryId) => {
					if (typeof categoryId === "number") return categoryId;
					if (typeof categoryId === "string") {
						const trimmed = categoryId.trim();
						if (!trimmed) return Number.NaN;
						return Number(trimmed);
					}
					return Number.NaN;
				})
				.filter((categoryId) => Number.isInteger(categoryId) && categoryId > 0),
		),
	);
}

export async function registerProvider(
	input: RegisterProviderInput,
): Promise<{ userId: number; providerId: number }> {
	const normalizedAddress = normalizeAddressText(input.address);
	const normalizedCategoryIds = normalizeCategoryIds(input.categoryIds);
	const userForm = new FormData();
	userForm.append("names", input.names);
	userForm.append("last_names", input.lastNames);
	userForm.append("password", input.password);
	userForm.append("email", input.email);
	// type_user viene del query param ?type=Proveedor en /users/register
	userForm.append("phone", input.phone);
	if (input.age !== undefined) {
		userForm.append("age", String(input.age));
	}
	userForm.append("addresses_user", normalizedAddress || input.address);
	if (input.dateOfBirth) {
		userForm.append("date_of_birth", input.dateOfBirth);
	}
	if (input.gender) {
		userForm.append("gender", input.gender);
	}
	if (input.photoFile) {
		userForm.append("image_profile", input.photoFile);
	}

	const userRes = await postFormData<CreateUserResponse>(
		"/users/register?type=Proveedor",
		userForm,
	);
	const createdUserId = extractEntityId(userRes, "user");

	const providerRes = await postJsonWithOptions<CreateProviderResponse>(
		"/providers",
		{
			user_id: createdUserId,
			company_name: input.companyName,
			rating: 0,
			experience_years:
				typeof input.experienceYears === "number" ? input.experienceYears : 0,
			...(input.description?.trim()
				? { description_provider: input.description.trim() }
				: {}),
			...(input.dateOfBirth ? { date_of_birth: input.dateOfBirth } : {}),
			...(input.gender ? { gender_provider: input.gender } : {}),
			category_ids: normalizedCategoryIds,
			availability: input.availability ?? [],
			ubicacion: input.address,
		},
		{ auth: false },
	);
	const createdProviderId = extractEntityId(providerRes, "provider");

	await persistStructuredAddress(createdUserId, normalizedAddress);

	const hasSession = !!getSession()?.user?.id;

	if (normalizedCategoryIds.length > 0) {
		savePendingProviderCategories(createdProviderId, normalizedCategoryIds);

		if (hasSession) {
			try {
				await Promise.all(
					normalizedCategoryIds.map(async (categoryId) => {
						await postJsonWithOptions("/categorie-provider", {
							category_id: categoryId,
							provider_id: createdProviderId,
						});
					}),
				);
				clearPendingProviderCategories(createdProviderId);
			} catch {
				// Keep pending categories for later sync after authentication.
			}
		}
	}

	const normalizedAvailability = Array.from(
		new Map(
			(input.availability ?? [])
				.filter((day) => day.enabled)
				.map((day) => [day.day, day]),
		).values(),
	);
	let schedulesPersisted = false;

	if (normalizedAvailability.length > 0) {
		if (hasSession) {
			try {
				await Promise.all(
					normalizedAvailability.map(async (day) => {
						const startTime = toTime24(day.startTime, day.startPeriod);
						const endTime = toTime24(day.endTime, day.endPeriod);
						if (!startTime || !endTime) {
							throw new Error(`Horario inválido para ${day.day}`);
						}

						await postJsonWithOptions("/schedules", {
							provider_id: createdProviderId,
							day_of_week: PROVIDER_DAY_MAP[day.day],
							start_time: startTime,
							end_time: endTime,
						});
					}),
				);
				schedulesPersisted = true;
			} catch (scheduleErr) {
				if (!isUnauthorizedError(scheduleErr)) {
					throw scheduleErr;
				}
			}
		}

		if (!schedulesPersisted) {
			savePendingProviderSchedule(createdProviderId, normalizedAvailability);
		}
	}

	return { userId: createdUserId, providerId: createdProviderId };
}
